class ElProductForm extends HTMLElement {
  constructor() {
    super();

    this.onSubmit = this.onSubmit.bind(this);
    this.onOptionChange = this.onOptionChange.bind(this);
    this.activeSubmitButton = null;
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    if (!this.form) return;

    this.submitButtons = this.querySelectorAll('[type="submit"]');
    this.subscriptionSubmitButtons = this.querySelectorAll(
      '[name="purchase_type"][value="subscription"]',
    );
    this.errorBox = this.querySelector("[data-error]");
    this.inputOptions = this.querySelectorAll("input[data-product-option]");
    this.variantIdInput = this.querySelector("[data-selected-variant-id]");
    this.oneTimeVariantIdInput = this.querySelector(
      "[data-one-time-variant-id]",
    );
    this.atcPrice = this.querySelector(".display__price");
    this.atcCompare = this.querySelector(".display__compare-price");

    this.sortVariantOptions();
    this.bindEvents();
    this.syncSelectedOption();
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener("submit", this.onSubmit);
    }

    this.submitButtons?.forEach((button) => {
      button.removeEventListener("click", this.onSubmitButtonClick);
    });

    this.inputOptions?.forEach((input) => {
      input.removeEventListener("change", this.onOptionChange);
    });
  }

  bindEvents() {
    this.onSubmitButtonClick = (event) => {
      this.activeSubmitButton = event.currentTarget;
    };

    this.submitButtons.forEach((button) => {
      button.addEventListener("click", this.onSubmitButtonClick);
    });

    this.inputOptions.forEach((input) => {
      input.addEventListener("change", this.onOptionChange);
    });

    this.form.addEventListener("submit", this.onSubmit);
  }

  sortVariantOptions() {
    const optionContainer = this.querySelector("[data-product-options]");
    if (!optionContainer) return;

    const variantOptions = Array.from(
      optionContainer.querySelectorAll("[data-variant-option]"),
    );

    if (variantOptions.length < 2) return;

    variantOptions
      .sort((firstOption, secondOption) => {
        const firstOrder = Number(firstOption.dataset.order || 999999);
        const secondOrder = Number(secondOption.dataset.order || 999999);
        return firstOrder - secondOrder;
      })
      .forEach((option) => optionContainer.appendChild(option));
  }

  get selectedOption() {
    return this.form?.querySelector("input[data-product-option]:checked") || null;
  }

  get selectedVariantId() {
    return (
      this.selectedOption?.dataset.variantId ||
      this.variantIdInput?.value ||
      null
    );
  }

  get selectedSellingPlanId() {
    return this.selectedOption?.dataset.sellingPlanId || null;
  }

  get selectedQuantity() {
    const quantity = Number.parseInt(
      this.selectedOption?.dataset.quantity || "1",
      10,
    );

    return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
  }

  get selectedPrice() {
    return this.selectedOption?.dataset.price || "";
  }

  get selectedComparePrice() {
    return this.selectedOption?.dataset.comparePrice || "";
  }

  get purchaseType() {
    if (this.activeSubmitButton?.value) {
      return this.activeSubmitButton.value;
    }

    const firstEnabledButton = Array.from(this.submitButtons).find(
      (button) => !button.disabled,
    );

    return firstEnabledButton?.value || "subscription";
  }

  get oneTimeVariantId() {
    return (
      this.activeSubmitButton?.dataset.oneTimeVariantId ||
      this.oneTimeVariantIdInput?.value ||
      null
    );
  }

  syncSelectedOption() {
    const selectedOption = this.selectedOption;

    if (selectedOption?.dataset.variantId && this.variantIdInput) {
      this.variantIdInput.value = selectedOption.dataset.variantId;
    }

    this.updateDisplayPrices();
    this.updateSubscriptionButtonState();
  }

  updateDisplayPrices() {
    if (this.atcPrice) {
      this.atcPrice.textContent = this.selectedPrice;
    }

    if (this.atcCompare) {
      this.atcCompare.textContent = this.selectedComparePrice;
      this.atcCompare.hidden = !this.selectedComparePrice;
    }
  }

  updateSubscriptionButtonState() {
    if (!this.subscriptionSubmitButtons.length) return;

    const selectedOption = this.selectedOption;
    const hasSellingPlan = Boolean(selectedOption?.dataset.sellingPlanId);
    const isAvailable = selectedOption?.dataset.available !== "false";
    const shouldDisable = !selectedOption || !hasSellingPlan || !isAvailable;

    this.subscriptionSubmitButtons.forEach((button) => {
      button.disabled = shouldDisable;
    });
  }

  onOptionChange(event) {
    const selectedInput = event.currentTarget;

    if (selectedInput.dataset.variantId && this.variantIdInput) {
      this.variantIdInput.value = selectedInput.dataset.variantId;
    }

    this.syncSelectedOption();
    this.clearError();

    this.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          variantId: selectedInput.dataset.variantId || null,
          sellingPlanId: selectedInput.dataset.sellingPlanId || null,
          price: selectedInput.dataset.price || "",
          comparePrice: selectedInput.dataset.comparePrice || "",
        },
      }),
    );
  }

  openCartDrawer() {
    const cartDrawer =
      document.querySelector("cart-drawer-component") ||
      document.querySelector("cart-drawer");

    if (!cartDrawer) return;

    if (typeof cartDrawer.open === "function") {
      cartDrawer.open();
      return;
    }

    if ("open" in cartDrawer) {
      cartDrawer.open = true;
    }

    cartDrawer.setAttribute("open", "");
    cartDrawer.setAttribute("aria-hidden", "false");
  }

  setLoadingState(isLoading) {
    this.submitButtons.forEach((button) => {
      if (isLoading) {
        button.disabled = true;
      } else if (
        button.value === "one_time" &&
        button.dataset.oneTimeAvailable === "false"
      ) {
        button.disabled = true;
      } else {
        button.disabled = false;
      }

      button.setAttribute("aria-busy", isLoading ? "true" : "false");
    });

    if (!isLoading) {
      this.updateSubscriptionButtonState();
    }
  }

  showError(message) {
    if (!this.errorBox) return;

    this.errorBox.hidden = false;
    this.errorBox.textContent = message;
  }

  clearError() {
    if (!this.errorBox) return;

    this.errorBox.hidden = true;
    this.errorBox.textContent = "";
  }

  async onSubmit(event) {
    event.preventDefault();
    this.clearError();

    const purchaseType = this.purchaseType;
    let payload;

    if (purchaseType === "one_time") {
      const oneTimeVariantId = this.oneTimeVariantId;
      const oneTimeAvailable =
        this.activeSubmitButton?.dataset.oneTimeAvailable !== "false";

      if (!oneTimeVariantId) {
        this.showError("One-time purchase product variant was not found.");
        return;
      }

      if (!oneTimeAvailable) {
        this.showError("The one-time purchase product is not available.");
        return;
      }

      payload = {
        id: oneTimeVariantId,
        quantity: 1,
      };
    } else {
      const variantId = this.selectedVariantId;
      const sellingPlanId = this.selectedSellingPlanId;

      if (!variantId) {
        this.showError("Please select a product option.");
        return;
      }

      if (!sellingPlanId) {
        this.showError(
          "The selected variant does not have an available selling plan.",
        );
        return;
      }

      payload = {
        id: variantId,
        quantity: this.selectedQuantity,
        selling_plan: sellingPlanId,
      };
    }

    this.setLoadingState(true);

    try {
      const addResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const addData = await addResponse.json();

      if (!addResponse.ok) {
        this.showError(
          addData.description ||
            addData.message ||
            "Unable to add this item to cart.",
        );
        return;
      }

      const cartResponse = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            data: {
              itemCount: cart.item_count,
              source: "product-form-component",
            },
          },
        }),
      );

      requestAnimationFrame(() => {
        this.openCartDrawer();
      });
    } catch (error) {
      console.error(error);
      this.showError("Something went wrong while adding to cart.");
    } finally {
      this.setLoadingState(false);
      this.activeSubmitButton = null;
    }
  }
}

if (!customElements.get("el-product-form")) {
  customElements.define("el-product-form", ElProductForm);
}
